import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Loader2, IdCard, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { authHeader, blobToDataUrl } from "@/lib/aadhaar";

const NAVY = "#0A192F", MAG = "#FF2E63", CYAN = "#00B8D4";
const FONT = "'Outfit','Noto Sans Devanagari',sans-serif";
const PROGRAM = { AI: "Artificial Intelligence", Robotics: "Robotics & Humanoids", UAV: "UAV / Drone Technology", Aerospace: "Aerospace Engineering" };
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");
export const verifyUrl = (a, examId) => `${window.location.origin}/verify-id/${a.verify_code}${examId ? `?exam=${examId}` : ""}`;

// Student photo + QR as data URLs so html2canvas can paint them into the PDF.
export const useDocAssets = (a, examId) => {
  const [photo, setPhoto] = useState(null);
  const [qr, setQr] = useState(null);
  useEffect(() => {
    api.get("/student/admission/photo", { ...authHeader("student"), responseType: "blob" })
      .then((r) => blobToDataUrl(r.data)).then(setPhoto).catch(() => setPhoto(""));
  }, []);
  useEffect(() => { QRCode.toDataURL(verifyUrl(a, examId), { margin: 1, width: 260 }).then(setQr); }, [a, examId]);
  return { photo, qr };
};

const Photo = ({ src, name, w, h }) => (src
  ? <img src={src} alt="" style={{ width: w, height: h, objectFit: "cover", borderRadius: 10, border: `3px solid ${MAG}` }} />
  : <div style={{ width: w, height: h, borderRadius: 10, border: `3px solid ${MAG}`, background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: w / 3, fontWeight: 900, color: NAVY }}>{name.split(" ").map((p) => p[0]).slice(0, 2).join("")}</div>);

const Row = ({ k, v }) => (
  <div style={{ display: "flex", fontSize: 19, lineHeight: 1.55 }}><span style={{ width: 150, color: "#64748b" }}>{k}</span><b style={{ color: NAVY }}>{v}</b></div>
);

const Brand = ({ size = 30 }) => (
  <div style={{ fontSize: size, fontWeight: 900, color: "#fff" }}><span style={{ color: MAG }}>Robo</span>Spark <span style={{ fontSize: size * 0.4, letterSpacing: 3, opacity: 0.8 }}>PRP PVT LTD</span></div>
);

const IdCardDoc = ({ a, photo, qr, innerRef }) => (
  <div ref={innerRef} style={{ width: 1011, height: 638, background: "#fff", fontFamily: FONT, position: "relative", overflow: "hidden", boxSizing: "border-box" }}>
    <div style={{ background: NAVY, height: 120, padding: "22px 36px", display: "flex", justifyContent: "space-between", alignItems: "center", boxSizing: "border-box" }}>
      <Brand />
      <div style={{ textAlign: "right", color: "#fff" }}><div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 2 }}>STUDENT ID CARD</div><div style={{ fontSize: 16, color: "#7dd3fc" }}>छात्र पहचान पत्र</div></div>
    </div>
    <div style={{ height: 6, background: `linear-gradient(90deg, ${MAG}, ${CYAN})` }} />
    <div style={{ display: "flex", gap: 34, padding: "30px 36px" }}>
      <Photo src={photo} name={a.student_name} w={190} h={230} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 34, fontWeight: 900, color: NAVY, marginBottom: 10 }}>{a.student_name}</div>
        <Row k="Student ID" v={a.student_code} /><Row k="Roll No." v={a.roll_no} /><Row k="Class" v={a.class_grade} />
        <Row k="Program" v={PROGRAM[a.program] || a.program} /><Row k="School" v={a.school_name} /><Row k="Date of birth" v={fmtDate(a.dob)} />
      </div>
      <div style={{ textAlign: "center", width: 180 }}>
        {qr && <img src={qr} alt="" style={{ width: 170, height: 170 }} />}
        <div style={{ fontSize: 12, color: "#64748b" }}>Scan to verify</div>
      </div>
    </div>
    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, background: "#f1f5f9", padding: "14px 36px", display: "flex", justifyContent: "space-between", fontSize: 14, color: "#334155", boxSizing: "border-box" }}>
      <span>Valid till <b>{fmtDate(a.valid_till)}</b> · Emergency: <b>{a.parent_phone}</b></span>
      <span>If found, return to RoboSpark PrP Pvt Ltd, C-30, Sector 63, Noida</span>
    </div>
  </div>
);

const AdmitDoc = ({ a, exam, photo, qr, innerRef }) => (
  <div ref={innerRef} style={{ width: 794, height: 1123, background: "#fff", fontFamily: FONT, boxSizing: "border-box", padding: 36 }}>
    <div style={{ border: `3px solid ${NAVY}`, height: "100%", boxSizing: "border-box" }}>
      <div style={{ background: NAVY, padding: "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Brand size={26} />
        <div style={{ textAlign: "right", color: "#fff" }}><div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 2 }}>ADMIT CARD</div><div style={{ fontSize: 14, color: "#7dd3fc" }}>प्रवेश पत्र</div></div>
      </div>
      <div style={{ height: 5, background: `linear-gradient(90deg, ${MAG}, ${CYAN})` }} />
      <div style={{ padding: "24px 28px" }}>
        <div style={{ fontSize: 26, fontWeight: 900, color: NAVY }}>{exam.title}</div>
        <div style={{ display: "flex", gap: 24, marginTop: 20 }}>
          <div style={{ flex: 1, fontSize: 16 }}>
            {[["Candidate", a.student_name], ["Roll No.", a.roll_no], ["Student ID", a.student_code], ["Class", a.class_grade], ["Program", PROGRAM[a.program] || a.program], ["School", a.school_name], ["Parent / guardian", a.parent_name]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", borderBottom: "1px solid #e2e8f0", padding: "8px 0" }}><span style={{ width: 170, color: "#64748b" }}>{k}</span><b style={{ color: NAVY }}>{v}</b></div>
            ))}
          </div>
          <div style={{ width: 170, textAlign: "center" }}><Photo src={photo} name={a.student_name} w={160} h={195} /></div>
        </div>
        <div style={{ marginTop: 24, background: "#f1f5f9", borderRadius: 14, padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 16, color: NAVY }}>
          <div>Date: <b>{fmtDate(exam.date)}</b></div><div>Reporting / start time: <b>{exam.start_time}</b></div>
          <div>Duration: <b>{exam.duration_min} minutes</b></div><div>Mode: <b style={{ textTransform: "capitalize" }}>{exam.mode}</b></div>
          <div style={{ gridColumn: "1 / -1" }}>Centre: <b>{exam.center}</b></div>
        </div>
        <div style={{ marginTop: 22, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>
          <b style={{ color: NAVY }}>Instructions · निर्देश</b>
          <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
            {(exam.instructions ? exam.instructions.split(/\n+/) : []).filter(Boolean).map((t, i) => <li key={i}>{t}</li>)}
            <li>Carry this admit card and your RoboSpark Student ID card. · यह एडमिट कार्ड और RoboSpark ID कार्ड साथ लाएँ।</li>
            <li>Report 20 minutes before the start time. Electronic devices are not allowed unless the exam needs them.</li>
          </ul>
        </div>
        <div style={{ marginTop: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ textAlign: "center" }}>{qr && <img src={qr} alt="" style={{ width: 130, height: 130 }} />}<div style={{ fontSize: 11, color: "#64748b" }}>Scan to verify this admit card</div></div>
          <div style={{ fontSize: 14, color: "#334155", textAlign: "center" }}><div style={{ borderTop: "1px solid #0A192F", width: 200, paddingTop: 4 }}>Candidate's signature</div></div>
          <div style={{ fontSize: 14, color: "#334155", textAlign: "center" }}><div style={{ borderTop: "1px solid #0A192F", width: 220, paddingTop: 4 }}>Controller of Examinations<br />RoboSpark PrP Pvt Ltd</div></div>
        </div>
      </div>
    </div>
  </div>
);

const savePdf = async (el, filename, w, h) => {
  const html2pdf = (await import("html2pdf.js")).default;
  await html2pdf().set({
    margin: 0, filename, image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: "px", format: [w, h], orientation: w > h ? "landscape" : "portrait", hotfixes: ["px_scaling"] },
  }).from(el).save();
};

const DocButton = ({ testid, label, icon: Icon, onClick, ready }) => {
  const [busy, setBusy] = useState(false);
  const { L } = useLang();
  const go = async () => { setBusy(true); try { await onClick(); } catch { toast.error(L("Could not create the PDF", "PDF नहीं बन सका")); } finally { setBusy(false); } };
  return (
    <Button onClick={go} disabled={busy || !ready} className="rs-cta rounded-full" data-testid={testid}>
      {busy || !ready ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Icon className="w-4 h-4 mr-1" />}{label}
    </Button>
  );
};

export const IdCardButton = ({ a }) => {
  const { L } = useLang();
  const ref = useRef(null);
  const { photo, qr } = useDocAssets(a);
  return (
    <>
      <DocButton testid="id-card-download-btn" icon={IdCard} ready={photo !== null && qr} label={L("Download ID card (PDF)", "ID कार्ड डाउनलोड करें (PDF)")}
        onClick={() => savePdf(ref.current, `RoboSpark-ID-${a.student_code}.pdf`, 1011, 638)} />
      <div aria-hidden style={{ position: "fixed", left: -12000, top: 0 }}><IdCardDoc a={a} photo={photo} qr={qr} innerRef={ref} /></div>
    </>
  );
};

export const AdmitCardButton = ({ a, exam }) => {
  const { L } = useLang();
  const ref = useRef(null);
  const { photo, qr } = useDocAssets(a, exam.id);
  return (
    <>
      <DocButton testid={`admit-card-download-${exam.id}`} icon={FileText} ready={photo !== null && qr} label={L("Admit card (PDF)", "एडमिट कार्ड (PDF)")}
        onClick={() => savePdf(ref.current, `RoboSpark-Admit-${a.roll_no}-${exam.date}.pdf`, 794, 1123)} />
      <div aria-hidden style={{ position: "fixed", left: -12000, top: 0 }}><AdmitDoc a={a} exam={exam} photo={photo} qr={qr} innerRef={ref} /></div>
    </>
  );
};
